import { calculateBIRReceiptDetails } from "../bir-receipt"
import ThermalPrinterEncoder, {
  ThermalPrinterEncoderOption,
} from "../../lib/thermal-printer-encoder"
import {
  encodeBirFooter,
  encodeBirHeader,
  formatPaymentMethod,
  printBorder,
  printSplitTable,
} from "./helper"
import { format, parseISO } from "date-fns"
import { BIR_LABEL, BIRReceiptDetails } from "../models"
import axios from "axios"
import Big from "big.js"
import {
  getActualCashChange,
  getActualCashReceived,
} from "../../hooks/api/use-cart-transaction"
import { BASE_URL } from "../../constants/Config"

export const encodeBIRReceipt = (
  details: BIRReceiptDetails,
  encoderOptions: ThermalPrinterEncoderOption = {
    language: "esc-pos",
    columns: 32,
    feedBeforeCut: 1,
  }
) => {
  if (details.type === "DEFAULT") {
    if (details.trx.orderStatus === "VOID") {
      return encodeBIRVoidReceipt(details, encoderOptions)
    } else if (details.trx.orderStatus === "RETURN_ACCEPTED") {
      return encodeBIRRefundReceipt(details, encoderOptions)
    }
  }

  return encodeDefaultBIRReceipt(details, encoderOptions)
}

export const encodeDefaultBIRReceipt = async (
  details: BIRReceiptDetails,
  encoderOptions: ThermalPrinterEncoderOption
) => {
  const width = encoderOptions.columns || 32

  const trx = details.trx

  const {
    customerLocation,
    totalTaxApplied,
    totalTaxExemption,
    vatSalesAmount,
    totalVatTax,
    vatExemptSales,
    zeroRatesSales,
    totalIncludedTaxes,
    originalORNumber,
    originalTransNumber,
  } = calculateBIRReceiptDetails(details.trx)

  let encoder = new ThermalPrinterEncoder({
    ...encoderOptions,
    imageMode: "raster",
  }).initializeWithMultiByte()

  const logo =
    details.invoiceSettings?.printLogo || details.business?.logoUrl || ""
  if (details.invoiceSettings.showLogo && logo) {
    try {
      const loadImage = (src: string) => {
        return new Promise<HTMLImageElement>((resolve, reject) => {
          axios
            .get(src, {
              responseType: "blob",
              headers: {
                "Cache-Control": "no-cache",
              },
            })
            .then((response) => {
              const img = new Image()
              img.crossOrigin = "anonymous"
              img.src = URL.createObjectURL(response.data)

              img.onload = () => {
                URL.revokeObjectURL(img.src) // Clean up
                resolve(img)
              }
              img.onerror = (err) => {
                URL.revokeObjectURL(img.src)
                reject(err)
              }
            })
        })
      }
      const image = await loadImage(logo)
      encoder = encoder
        .align("center")
        .image(image, 160, 160, "atkinson")
        .newline()
        .newline()
    } catch (e) {
      console.log(e)
    }
  }

  if (details.invoiceSettings.showCustomerQrCode === "Header") {
    encoder = encoder
      .newline()
      .align("center")
      .line("Scan to access your portal")
      .newline()
      .qrcode(
        details.trx.customer?.id
          ? `${BASE_URL}/customer-portal/${details.trx.customer?.id}?currency=${details.business?.businessCurrency}`
          : `${BASE_URL}/share/receipt/${details.trx.id}`
      )
  }

  encoder = encodeBirHeader(encoder, {
    cashRegister: details.trx.cashRegister,
    location: details.trx.location,
    business: details.business,
    isService: details.trx.isServiceOrder,
    hasTaxes: details.locationHasTaxes,
    locationVatType: details.locationVatType,
  })
    .align("center")
    .newline()
    .newline()

  const isReprint =
    !!details.trx.receiptHistory?.filter((r) => r.printed)?.length &&
    details.type !== "ORIGINAL"
  if (isReprint) {
    encoder = encoder
      .bold(true)
      .line("*****REPRINT*****")
      .bold(false)
      .newline()
      .line(format(new Date(), "MM/dd/yyyy HH:mm:ss"))
  }

  //disclaimer at header
  if (
    details.invoiceSettings.showDisclaimer === "Header" &&
    details.invoiceSettings.disclaimer
  ) {
    encoder = encoder
      .align("center")
      .line(`Disclaimer: ${details.invoiceSettings.disclaimer || ""}`)
  }

  encoder = printBorder(encoder, width, { noSpacing: true })
  const receiptDetails = [
    ["Date:", format(parseISO(details.trx.createdAt), "MM/dd/yyyy HH:mm:ss")],
  ]

  const hideInvoice = (details.trx.outstanding || 0) > 0

  if (!hideInvoice) {
    receiptDetails.push(["Invoice No.:", ""])
    receiptDetails.push([originalORNumber || details.nextORNumber || "", ""])
  }

  const { paymentMethodCode, paymentChannel } = details.trx

  if (
    details.invoiceSettings.showReferenceNumber &&
    details.trx.paymentId &&
    paymentMethodCode === "CUSTOM"
  ) {
    receiptDetails.push(["Reference No.:", ""])
    receiptDetails.push([details.trx.paymentId, ""])
  }
  if (paymentMethodCode === "SPLIT_PAYMENT") {
    const splitPaymentDetails = details.trx.splitPaymentDetails
    if (splitPaymentDetails && splitPaymentDetails.length) {
      receiptDetails.push(["Split Payments", ""])
      splitPaymentDetails.forEach((el) => {
        if (
          details.invoiceSettings.showReferenceNumber &&
          el.paymentId &&
          el.paymentId !== ""
        ) {
          receiptDetails.push(["Reference No.:", ""])
          receiptDetails.push([el.paymentId, ""])
        }
        const paymentMethod = formatPaymentMethod(
          el.paymentMethodCode,
          el.paymentChannel || el.paymentId
        )
        receiptDetails.push([paymentMethod, `${el.amount}`])
      })
    }
  }

  if (trx?.tables?.length) {
    trx?.tables?.forEach((table, key) => {
      let l = ""
      if (key < 1) {
        l = trx?.tables && trx?.tables?.length > 1 ? "TABLES" : "TABLE"
      }

      receiptDetails.push([l, table?.name])
    })
  }

  encoder = encoder.table(
    [
      { width: 13 - 1, marginRight: 2, align: "left" },
      { width: width - 13 - 1, align: "right" },
    ],
    receiptDetails
  )
  encoder = printBorder(encoder, width, { noSpacing: true })

  encoder = encoder.table(
    [
      { width: width - 17 - 1, marginRight: 2, align: "left" },
      { width: 17 - 1, align: "right" },
    ],
    [
      [
        `Staff: ${details.trx.createdBy}`,
        hideInvoice
          ? ""
          : `Trans No. ${originalTransNumber || details.nextSeriesNumber || "-"}`,
      ],
      [formatPaymentMethod(paymentMethodCode, paymentChannel), ""],
    ]
  )
  encoder = printBorder(encoder, width, { noSpacing: true })

  if (details.trx.shipment?.shipmentType === "POS_MANUAL") {
    encoder = printBorder(encoder, width, { noSpacing: true })

    const { quoteDetails } = details.trx.shipment
    const {
      bookingCode,
      customerName,
      address,
      city,
      phoneNumber,
      courierName,
    } = quoteDetails
    encoder = printSplitTable(encoder, width, width / 2, [
      [
        `Order Number: ${details.trx.orderNumber}`,
        `Booking Code: ${bookingCode}`,
      ],
    ])
      .align("left")
      .line(`Customer: ${customerName || "-"}`)
      .line(`Address: ${address || "-"}`)
      .line(`City: ${city || "-"}`)
      .line(`Phone Number: ${phoneNumber || "-"}`)

    if (courierName) {
      encoder = encoder.line(`Courier Name: ${courierName}`)
    }

    encoder = printBorder(encoder, width, { noSpacing: true })
  }

  let pickupTimeFormatted = ""
  if (details.trx.isPickup && details.trx.pickupTime) {
    try {
      pickupTimeFormatted = format(
        parseISO(details.trx.pickupTime),
        "dd/MM/yyyy"
      )
    } catch (_) {}
  }

  if (pickupTimeFormatted) {
    encoder = encoder.line(`Picked up: ${pickupTimeFormatted}`)
  }

  if (details.invoiceSettings.showCustomerInfo === "Header") {
    encoder = encoder.table(
      [
        { width: 12 - 1, marginRight: 2, align: "left" },
        { width: width - 12 - 1, align: "right" },
      ],
      [
        [`Customer:`, details.trx.customer?.fullName || "Walk-in Customer"],
        ["ID No. :", details.trx.customer?.membershipCode || ""],
        ["ADDRESS:", customerLocation?.addressLine1 || ""],
        ["TIN:", details.trx.customer?.taxId || ""],
        ["SC ID No.:", details.trx.customer?.seniorCitizenId || ""],
      ]
    )

    if (
      details.trx.rewardedPoints != null ||
      details.trx.customer?.points != null ||
      details.trx.rewardedCashback != null ||
      details.trx.customer?.cashback != null
    ) {
      if (details.trx.rewardedPoints != null) {
        encoder = encoder
          .align("left")
          .line("Rewarded Points: " + details.trx.rewardedPoints)
      }
      if (details.trx.customer?.points != null) {
        encoder = encoder
          .align("left")
          .line("Total Points: " + details.trx.customer?.points)
      }
      if (details.trx.rewardedCashback != null) {
        encoder = encoder
          .align("left")
          .line("Rewarded Cashback: " + details.trx.rewardedCashback)
      }
      if (details.trx.customer?.cashback != null) {
        encoder = encoder
          .align("left")
          .line("Total Cashback: " + details.trx.customer?.cashback)
      }
    }
    //add signature line
    encoder = encoder.align("left").line("Signature:").newline()
    encoder = printBorder(encoder, width, { noSpacing: true })
  }

  if (details.invoiceSettings.showBarcode === "Header") {
    encoder = encoder
      .newline()
      .align("center")
      .barcode(trx.orderNumber.toString(), "code39", {
        width: 1,
        text: true,
      })
  }

  encoder = encoder
    .table(
      [
        { width: 20 - 1, marginRight: 2, align: "left" },
        { width: width - 20 - 1, align: "right" },
      ],
      [
        [
          "Item(s)",
          details.trx.orderedProducts
            .reduce((acc, p) => acc + p.quantity, 0)
            .toString(),
        ],
      ]
    )
    .newline()

  encoder = encoder.align("left")
  details.trx.orderedProducts.forEach((p) => {
    encoder = encoder.table(
      [
        { width: 20 - 1, marginRight: 2, align: "left" },
        { width: width - 20 - 1, align: "right" },
      ],
      [
        [
          `${p.customProductName ?? p.name} - ${p.quantity}x @${p.sellingPrice}`,
          p.totalPrice.toFixed(2),
        ],
      ]
    )

    const variantName = p.productOptionValues?.map((el) => el.name).join(" / ")
    if (variantName) {
      encoder = encoder.line(`(${variantName})`)
    }

    if (p.serialNumber && p.serialNumber.length > 0) {
      encoder = encoder.line(`Serial Number:`)
      p.serialNumber.forEach((el) => {
        encoder = encoder.line(`${el}`)
      })
    }

    if (p.notes) {
      encoder = encoder.line(`Notes: ${p.notes}`)
    }

    if (!!p.orderedProductAddOns?.length) {
      encoder = encoder.line("Add Ons:")

      p.orderedProductAddOns.forEach((el) => {
        const totalPrice = Big(el.quantity).mul(el.sellingPrice).toFixed(2)
        encoder = encoder.table(
          [
            { width: 20 - 1, marginRight: 2, align: "left" },
            { width: width - 20 - 1, align: "right" },
          ],
          [
            [
              `${el.name} - ${el.quantity}x@${el.sellingPrice.toLocaleString()}`,
              totalPrice,
            ],
          ]
        )
      })
    }

    encoder = encoder.newline()
  })

  const discount = details.trx.nonTaxDiscounts || details.trx.discount

  let summaryDetails = [
    [`Subtotal`, details.trx.totalCartAmount.toFixed(2)],
    ...(details.trx.serviceCharge
      ? [["Service Charge", details.trx.serviceCharge.toFixed(2)]]
      : []),
    [`Total Sales`, details.trx.totalCartAmount.toFixed(2)],
  ]
  const { cashbackApplied } = details.trx
  if (cashbackApplied) {
    summaryDetails.push(["Cashback Applied", `${cashbackApplied}`])
  } else if (discount) {
    const discountAmount = details.trx.promotionDetails?.[0]?.discountPercentage
      ? `- ${details.trx.promotionDetails?.[0]?.discountPercentage}%`
      : ""
    summaryDetails.push([
      (BIR_LABEL[details.trx.promotionDetails?.[0]?.code || ""] ||
        details.trx.promotionDetails?.[0]?.code ||
        "Discount") + discountAmount,
      details.trx.nonTaxDiscounts?.toFixed(2) || "0",
    ])
  }
  details.trx.taxes?.forEach((t) => {
    summaryDetails.push([
      `- ${t.name} ${t.rate}% ${t.type === "INCLUDED" ? "(Included)" : ""}`,
      t.amount.toFixed(2),
    ])
  })
  summaryDetails.push(["Total amount due", details.trx.grandTotal.toFixed(2)])
  encoder = encoder
    .table(
      [
        { width: 20 - 1, marginRight: 2, align: "left" },
        { width: width - 20 - 1, align: "right" },
      ],
      summaryDetails
    )
    .newline()
    .table(
      [
        { width: width / 2 - 1, marginRight: 2, align: "left" },
        { width: width / 2 - 1, align: "right" },
      ],
      [
        ...(trx.orderStatus !== "PAID" && trx.outstanding && trx.outstanding > 0
          ? [["Outstanding", trx.outstanding.toFixed(2)]]
          : []),
        ["Amount Received", getActualCashReceived(details.trx).toFixed(2)],
        ["Change", getActualCashChange(details.trx).toFixed(2)],
      ]
    )
    .newline()

  encoder = printBorder(encoder, width, { noSpacing: true })
  const isVatRegistered =
    details?.trx?.location?.isVatRegistered &&
    details.trx.location?.isVatRegistered.toLowerCase() === "yes"

  encoder = encoder.table(
    [
      { width: width / 4, align: "left" },
      { width: width / 4, align: "right" },
      { width: width / 4, align: "right" },
      { width: width / 4, align: "right" },
    ],
    [
      ["", "Net.Amt", `${isVatRegistered ? "VAT" : ""}`, "Amount"],
      [
        "VATABLE",
        (vatSalesAmount - totalVatTax).toFixed(2),
        isVatRegistered ? totalVatTax.toFixed(2) : "",
        vatSalesAmount.toFixed(2),
      ],
      [
        "EXEMPT",
        vatExemptSales.toFixed(2),
        isVatRegistered ? totalTaxExemption.toFixed(2) : "",
        (vatExemptSales + totalTaxExemption).toFixed(2),
      ],
      [
        "ZERO-R",
        zeroRatesSales.toFixed(2),
        `${isVatRegistered ? "0.00" : ""}`,
        zeroRatesSales.toFixed(2),
      ],
    ]
  )

  // if (details.trx.location.isVatRegistered === "Yes") {
  //   encoder = printBorder(encoder, width, { noSpacing: true })
  //   encoder = encoder
  //     .newline()
  //     .line("THIS DOCUMENT IS NOT VALID FOR CLAIM OF INPUT TAX")
  //     .newline()
  //   encoder = printBorder(encoder, width, { noSpacing: true })
  // }

  if (details.invoiceSettings.message) {
    encoder = encoder
      .newline()
      .align("center")
      .line(details.invoiceSettings.message)
      .newline()
  }

  encoder = encodeBirFooter(
    encoder,
    details.locationHasTaxes,
    details.fullCashRegister || details.trx.cashRegister
  ).newline()

  //disclaimer at footer
  if (
    details.invoiceSettings.disclaimer &&
    details.invoiceSettings.showDisclaimer === "Footer"
  ) {
    encoder = encoder
      .align("center")
      .line(`Disclaimer: ${details.invoiceSettings.disclaimer || ""}`)
  }

  if (details.invoiceSettings.showBarcode === "Footer") {
    encoder = encoder
      .align("center")
      .barcode(trx.orderNumber.toString(), "code39", {
        width: 1,
        text: true,
      })
  }

  if (details.invoiceSettings.showCustomerInfo === "Footer") {
    encoder = printBorder(encoder, width, { noSpacing: true })
    encoder = encoder.table(
      [
        { width: 12 - 1, marginRight: 2, align: "left" },
        { width: width - 12 - 1, align: "right" },
      ],
      [
        [`Customer:`, details.trx.customer?.fullName || "Walk-in Customer"],
        ["ID No. :", details.trx.customer?.membershipCode || ""],
        ["ADDRESS:", customerLocation?.addressLine1 || ""],
        ["TIN:", details.trx.customer?.taxId || ""],
        ["SC ID No.:", details.trx.customer?.seniorCitizenId || ""],
      ]
    )

    if (
      details.trx.rewardedPoints != null ||
      details.trx.customer?.points != null ||
      details.trx.rewardedCashback != null ||
      details.trx.customer?.cashback != null
    ) {
      if (details.trx.rewardedPoints != null) {
        encoder = encoder
          .align("left")
          .line("Rewarded Points: " + details.trx.rewardedPoints)
      }
      if (details.trx.customer?.points != null) {
        encoder = encoder
          .align("left")
          .line("Total Points: " + details.trx.customer?.points)
      }
      if (details.trx.rewardedCashback != null) {
        encoder = encoder
          .align("left")
          .line("Rewarded Cashback: " + details.trx.rewardedCashback)
      }
      if (details.trx.customer?.cashback != null) {
        encoder = encoder
          .align("left")
          .line("Total Cashback: " + details.trx.customer?.cashback)
      }
    }
    //add signature line
    encoder = encoder.align("left").line("Signature:").newline()
  }

  if (details.trx.queueNumber) {
    encoder = encoder
      .align("center")
      .line(`Order #: ${details.trx.queueNumber}`)
  }

  if (details.invoiceSettings.showCustomerQrCode === "Footer") {
    encoder = encoder
      .newline()
      .align("center")
      .line("Scan to access your portal")
      .newline()
      .qrcode(
        details.trx.customer?.id
          ? `${BASE_URL}/customer-portal/${details.trx.customer?.id}?currency=${details.business?.businessCurrency}`
          : `${BASE_URL}/share/receipt/${details.trx.id}`
      )
  }

  return encoder.newline(3).cut().encode()
}

export const encodeBIRVoidReceipt = (
  details: BIRReceiptDetails,
  encoderOptions: ThermalPrinterEncoderOption
) => {
  const width = encoderOptions.columns || 32

  const trx = details.trx
  const { originalORNumber, originalTransNumber } = calculateBIRReceiptDetails(
    details.trx
  )

  let encoder = new ThermalPrinterEncoder(
    encoderOptions
  ).initializeWithMultiByte()

  const originalReceipt = details.trx.receiptHistory?.find(
    (el) => el.type === "VOID"
  )

  encoder = encodeBirHeader(encoder, {
    cashRegister: details.trx.cashRegister,
    location: details.trx.location,
    business: details.business,
    isService: details.trx.isServiceOrder,
    hasTaxes: details.locationHasTaxes,
    locationVatType: details.locationVatType,
  })
    .align("center")
    .line("VOID TRANS")
    // .line("Official Receipt Number" + details.trx.orderNumber)
    .line("*****VOID*****")
    .line(
      "Void Trans No. " +
        (originalReceipt?.seriesNumber || details.nextSeriesNumber || "-")
    )
    .newline()
    .newline()

  //disclaimer at header
  if (
    details.invoiceSettings.showDisclaimer === "Header" &&
    details.invoiceSettings.disclaimer
  ) {
    encoder = encoder
      .align("center")
      .line(`Disclaimer: ${details.invoiceSettings.disclaimer || ""}`)
  }

  encoder = printBorder(encoder, width, { noSpacing: true })
  encoder = encoder.table(
    [
      { width: 13 - 1, marginRight: 2, align: "left" },
      { width: width - 13 - 1, align: "right" },
    ],
    [
      ["Date:", format(parseISO(details.trx.createdAt), "MM/dd/yyyy HH:mm:ss")],
      ["Invoice No.:", ""],
      [originalORNumber || details.nextORNumber || "", ""],
    ]
  )
  encoder = printBorder(encoder, width, { noSpacing: true })

  encoder = encoder.table(
    [
      { width: width - 17 - 1, marginRight: 2, align: "left" },
      { width: 17 - 1, align: "right" },
    ],
    [
      [
        `Staff: ${details.trx.createdBy}`,
        `Trans No. ${originalTransNumber ?? "-"}`,
      ],
      [
        formatPaymentMethod(
          details.trx.paymentMethodCode,
          details.trx.paymentChannel
        ),
        "",
      ],
    ]
  )
  encoder = printBorder(encoder, width, { noSpacing: true })

  encoder = encoder
    .table(
      [
        { width: width / 2 - 1, marginRight: 2, align: "left" },
        { width: width / 2 - 1, align: "right" },
      ],
      [
        ...(trx.orderStatus !== "PAID" && trx.outstanding && trx.outstanding > 0
          ? [["Outstanding", (trx.outstanding || 0).toFixed(2)]]
          : []),
        ["Amount Received", getActualCashReceived(details.trx).toFixed(2)],
        ["Change", getActualCashChange(details.trx).toFixed(2)],
      ]
    )
    .newline()

  encoder = encoder
    .table(
      [
        { width: width / 4, align: "left" },
        { width: width / 4, align: "right" },
        { width: width / 4, align: "right" },
        { width: width / 4, align: "right" },
      ],
      [
        ["Item", "QTY", "Price", "Amount"],
        ...details.trx.orderedProducts.map((p) => {
          let name = `${p.name}`

          return [
            name,
            p.quantity.toString(),
            p.sellingPrice.toFixed(2),
            p.totalPrice.toFixed(2),
          ]
        }),
      ]
    )
    .newline()

  const discount = details.trx.nonTaxDiscounts || details.trx.discount
  const promotionDetails = details.trx.promotionDetails?.[0]
  const discountRate = promotionDetails?.discountPercentage || 0
  let discountName = ["bir-pwd-food", "bir-pwd-retail"].includes(
    promotionDetails?.code || ""
  )
    ? "SC/PWD"
    : "Discount"
  if (discountRate > 0) {
    discountName = `${discountName} - ${discountRate}%`
  }

  encoder = encoder
    .table(
      [
        { width: width / 2 - 1, marginRight: 2, align: "left" },
        { width: width / 2 - 1, align: "right" },
      ],
      [
        ["Subtotal", details.trx.totalCartAmount.toFixed(2)],
        ...(discount > 0 ? [[discountName, discount.toFixed(2)]] : []),
        ...(details.trx.serviceCharge
          ? [["Service Charge", details.trx.serviceCharge.toFixed(2)]]
          : []),
        ["Tax", details.trx.tax.toFixed(2)],
        ["Total Amount", details.trx.grandTotal.toFixed(2)],
      ]
    )
    .newline()
    .table(
      [
        { width: width / 2 - 1, marginRight: 2, align: "left" },
        { width: width / 2 - 1, align: "right" },
      ],
      [
        ["Authorized Signature:", "_________"],
        ["Address:", "_________"],
        ["Phone #:", "_________"],
        ["Reason", "_________"],
      ]
    )
    .newline()

  encoder = encodeBirFooter(
    encoder,
    details.locationHasTaxes,
    details.fullCashRegister || details.trx.cashRegister
  )
    .newline()
    .line(
      "THIS RECEIPT SHALL BE VALID FOR FIVE (5) YEARS FROM THE DATE OF THE PERMIT TO USE"
    )

  if (
    details.invoiceSettings.showDisclaimer === "Footer" &&
    details.invoiceSettings.disclaimer
  ) {
    encoder = encoder
      .newline()
      .newline()
      .align("center")
      .line(`Disclaimer: ${details.invoiceSettings.disclaimer || ""}`)
  }

  return encoder.newline(3).cut().encode()
}

export const encodeBIRRefundReceipt = (
  details: BIRReceiptDetails,
  encoderOptions: ThermalPrinterEncoderOption
) => {
  const width = encoderOptions.columns || 32

  const trx = details.trx

  const { originalORNumber, originalTransNumber } = calculateBIRReceiptDetails(
    details.trx
  )

  const originalReceipt = details.trx.receiptHistory?.find(
    (el) => el.type === "REFUND"
  )

  let encoder = new ThermalPrinterEncoder(
    encoderOptions
  ).initializeWithMultiByte()
  encoder = encodeBirHeader(encoder, {
    cashRegister: details.trx.cashRegister,
    location: details.trx.location,
    business: details.business,
    isService: details.trx.isServiceOrder,
    hasTaxes: details.locationHasTaxes,
    locationVatType: details.locationVatType,
  })
    .align("center")
    .line("REFUND TRANS" + details.trx.cashRegister?.serialNo)
    .line("Official Receipt Number" + details.trx.orderNumber)
    .line("*****REFUND*****")
    .line(
      "Refund Trans No. " +
        (originalReceipt?.seriesNumber || details.nextSeriesNumber || "-")
    )
    .newline()
    .newline()

  //disclaimer at header
  if (
    details.invoiceSettings.showDisclaimer === "Header" &&
    details.invoiceSettings.disclaimer
  ) {
    encoder = encoder
      .align("center")
      .line(`Disclaimer: ${details.invoiceSettings.disclaimer || ""}`)
  }

  encoder = printBorder(encoder, width, { noSpacing: true })
  encoder = encoder.table(
    [
      { width: 13 - 1, marginRight: 2, align: "left" },
      { width: width - 13 - 1, align: "right" },
    ],
    [
      ["Date:", format(parseISO(details.trx.createdAt), "MM/dd/yyyy HH:mm:ss")],
      ["Invoice No.:", ""],
      [originalORNumber || details.nextORNumber || "", ""],
    ]
  )
  encoder = printBorder(encoder, width, { noSpacing: true })

  encoder = encoder.table(
    [
      { width: width - 17 - 1, marginRight: 2, align: "left" },
      { width: 17 - 1, align: "right" },
    ],
    [
      [
        `Staff: ${details.trx.createdBy}`,
        `Trans No. ${originalTransNumber || details.nextSeriesNumber || "-"}`,
      ],
      [
        formatPaymentMethod(
          details.trx.paymentMethodCode,
          details.trx.paymentChannel
        ),
        "",
      ],
    ]
  )
  encoder = printBorder(encoder, width, { noSpacing: true })

  encoder = encoder
    .table(
      [
        { width: width / 2 - 1, marginRight: 2, align: "left" },
        { width: width / 2 - 1, align: "right" },
      ],
      [
        ...(trx.orderStatus !== "PAID" && trx.outstanding && trx.outstanding > 0
          ? [["Outstanding", (trx.outstanding || 0).toFixed(2)]]
          : []),
        ["Amount Received", getActualCashReceived(details.trx).toFixed(2)],
        ["Change", getActualCashChange(details.trx).toFixed(2)],
      ]
    )
    .newline()

  encoder = encoder
    .table(
      [
        { width: width / 4, align: "left" },
        { width: width / 4, align: "right" },
        { width: width / 4, align: "right" },
        { width: width / 4, align: "right" },
      ],
      [
        ["Item", "QTY", "Price", "Amount"],
        ...details.trx.orderedProducts.map((p) => {
          let name = `${p.name}`

          return [
            name,
            p.quantity.toString(),
            p.sellingPrice.toFixed(2),
            p.totalPrice.toFixed(2),
          ]
        }),
      ]
    )
    .newline()

  const discount = details.trx.nonTaxDiscounts || details.trx.discount
  const promotionDetails = details.trx.promotionDetails?.[0]
  const discountRate = promotionDetails?.discountPercentage || 0
  let discountName = ["bir-pwd-food", "bir-pwd-retail"].includes(
    promotionDetails?.code || ""
  )
    ? "SC/PWD"
    : "Discount"
  if (discountRate > 0) {
    discountName = `${discountName} - ${discountRate}%`
  }

  encoder = encoder
    .table(
      [
        { width: width / 2 - 1, marginRight: 2, align: "left" },
        { width: width / 2 - 1, align: "right" },
      ],
      [
        ["Subtotal", details.trx.totalCartAmount.toFixed(2)],
        ...(discount > 0 ? [[discountName, discount.toFixed(2)]] : []),
        ["Tax", details.trx.tax.toFixed(2)],
        ["Total Amount", details.trx.grandTotal.toFixed(2)],
      ]
    )
    .newline()
    .table(
      [
        { width: width / 2 - 1, marginRight: 2, align: "left" },
        { width: width / 2 - 1, align: "right" },
      ],
      [
        ["Authorized Signature:", "_________"],
        ["Address:", "_________"],
        ["Phone #:", "_________"],
        ["Reason", "_________"],
      ]
    )
    .newline()

  encoder = encodeBirFooter(
    encoder,
    details.locationHasTaxes,
    details.fullCashRegister || details.trx.cashRegister
  )
    .newline()
    .line(
      "THIS RECEIPT SHALL BE VALID FOR FIVE (5) YEARS FROM THE DATE OF THE PERMIT TO USE"
    )

  if (
    details.invoiceSettings.disclaimer &&
    details.invoiceSettings.showDisclaimer === "Footer"
  ) {
    encoder = encoder
      .newline()
      .newline()
      .align("center")
      .line(`Disclaimer: ${details.invoiceSettings.disclaimer || ""}`)
  }

  return encoder.newline().newline().newline().cut().encode()
}
